import { map } from "rxjs";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
	Auth,
	sendPasswordResetEmail,
	UserCredential,
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
} from "@angular/fire/auth";
import {
	Firestore,
	doc,
	getDoc,
	deleteDoc,
	setDoc,
	updateDoc,
	serverTimestamp,
	documentId,
	docSnapshots,
	collection,
	getDocs,
	query,
	where,
	runTransaction,
} from "@angular/fire/firestore";
import {
	AlertController,
	ModalController,
	NavController,
	ToastController,
} from "@ionic/angular";
import { getApps, initializeApp } from "@angular/fire/app";
import { getMessaging, isSupported, MessagePayload, onMessage } from "@angular/fire/messaging";
import { Subject, lastValueFrom, from } from "rxjs";
import { Timestamp } from "../models/timestamp";
import { User, UserFcmToken } from "../models/user";
import { takeUntil } from "rxjs/operators";
import { getDownloadURL, Storage, ref } from "@angular/fire/storage";
import { SquareItem } from "../models/squareItem";
import { environment } from "src/environments/environment";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { SquarePlan } from "../models/squarePlan";
import { estimateRemainingTime } from "src/app/shared/util/migrate";
import { AppUser } from "../models/appUser";
import { RiderEmail } from "../models/riderEmail";
import { RiderPrivateContact } from "../models/riderPrivateContact";
import { EulaAgreementModal } from "src/app/shared/modals/eula-agreement-modal/eula-agreement-modal";
import { AgreedToEula } from "../models/agreedToEula";
import { onAuthStateChanged } from "@angular/fire/auth";
import { getClientId } from "../models/util/client-id.util";
import {
	getTierForFeature,
	hasTierAtLeast,
	SubscriptionFeature,
	SubscriptionStatus,
} from "../models/entitlement";
import { normalizePhoneNumber } from "../utils/phone-number";
import { isWebBrowser, supportsBrowserNotifications, supportsBrowserServiceWorker } from "../utils/browser-support";

export interface RiderMigrationRequest {
	sourceTenantId: string;
	destinationTenantId: string;
	dryRun: boolean;
}

export interface RiderMigrationSample {
	uid: string;
	name: string;
	riderNumber: string;
	email: string;
	rfidCount: number;
	status: string;
	reason: string;
	matchedBy: string[];
	matchedDestinationUids: string[];
}

export interface RiderMigrationResponse {
	sourceTenantId: string;
	sourceProjectId?: string;
	destinationTenantId: string;
	destinationProjectId?: string;
	dryRun: boolean;
	totalSourceRiders: number;
	willCreate: number;
	alreadyExists: number;
	conflicts: number;
	skipped: number;
	createdCount: number;
	samples: {
		willCreate: RiderMigrationSample[];
		alreadyExists: RiderMigrationSample[];
		conflicts: RiderMigrationSample[];
		skipped: RiderMigrationSample[];
	};
	warnings: string[];
}

export interface CreateCentralRiderProfileRequest {
	firstName: string;
	lastName: string;
	riderNumber?: string;
	bike?: string;
	dob?: string;
	privateContact: RiderPrivateContact;
}

export interface CreateCentralRiderProfileResponse {
	appUser: AppUser;
	riderEmail: RiderEmail;
}

@Injectable({
	providedIn: "root",
})
export class UserService {
	public defaultUserIcon: string = "assets/defaultProfile.jpg";
	public loggedIn: boolean = false;
	isApp: boolean = false;
	isAndroid: boolean = false;
	loggedInUser: User = new User();
	private loggedInUserPlanUnlockedItems: string[] = [];
	public appUsers: Array<AppUser> = [];
	appVersion: string = "1.8.005";
	compatibleVersion: boolean = false;
	serverVersion: string = "";
	userUpdateSubject: Subject<User>;
	public requestedRoute: string = "";
	public refreshRequestedRoute: string = "";
	public refreshRequestedTab: string = "";
	public selectedProduct: string = "";
	public loadingLogin: boolean = false;
	public authorizedUser: boolean = false;
	public shoppingCart: SquareItem = null;
	private destroyed$: Subject<boolean> = new Subject();
	private allUsersForFind: Array<User> = [];
	public eulaVersion: string = "1.0";
	public authReady = false;
	public authReadyTimedOut = false;
	private loggingOut = false;
	private readonly pendingFcmTokenWrites = new Set<Promise<void>>();
	private notificationUserUid = "";
	private unsubscribeMessages?: () => void;
	private notificationClickListener?: (event: MessageEvent) => void;
	private readonly presentedNotificationIds = new Set<string>();
	constructor(
		public afs: Firestore,
		public alertCtrl: AlertController,
		public afAuth: Auth,
		public httpClient: HttpClient,
		public storage: Storage,
		private fns: Functions,
		public navCtrl: NavController,
		public modalCtrl: ModalController,
		private toastCtrl: ToastController,
	) {}

	async waitForAuthReady(timeoutMs: number = 8000): Promise<void> {
		if (this.authReady) return;

		await new Promise<void>((resolve) => {
			let settled = false;
			let unsub: (() => void) | undefined;
			let timeoutId: ReturnType<typeof setTimeout> | undefined;

			const settle = (timedOut: boolean) => {
				if (settled) return;
				settled = true;
				this.authReadyTimedOut = timedOut;
				if (timeoutId) {
					clearTimeout(timeoutId);
				}
				if (unsub) {
					unsub();
				}
				if (timedOut) {
					console.warn("Auth ready timed out", { timeoutMs });
				}
				resolve();
			};

			timeoutId = setTimeout(() => settle(true), timeoutMs);
			unsub = onAuthStateChanged(this.afAuth, () => settle(false));
		});

		this.authReady = true;
	}

	async ensureLoggedInUserLoaded(uid: string): Promise<boolean> {
		if (
			this.loggedIn &&
			this.loggedInUser?.uid === uid &&
			this.loggedInUser?.role
		) {
			return true;
		}

		try {
			const snap = await getDoc(doc(this.afs, `users/${uid}`));
			if (!snap.exists()) return false;

			this.loggedInUser = new User().buildUser(snap.data() as User);
			if (!this.loggedInUser.uid) {
				this.loggedInUser.uid = uid;
			}

			this.loggedIn = true;
			this.loggedInUser.emailVerified = new Timestamp(Date.now() / 1000, 0);

			this.setAuthLevel();
			await this.setUserUnlockedItems();
			this.userSub();

			return true;
		} catch (err) {
			console.log("ensureLoggedInUserLoaded error", err);
			return false;
		}
	}
	getAppVersion() {
		return docSnapshots(doc(this.afs, "appConfig/versioning"));
	}

	async isVersionUpToDate() {
		let version: string = await this.getVersion();

		let cloudVersion = parseInt(version.replace(/\./g, "").substr(0, 5));
		let localVersion = parseInt(
			this.appVersion.replace(/\./g, "").substr(0, 5),
		);

		this.serverVersion = version;

		if (cloudVersion <= localVersion) {
			this.compatibleVersion = true;
			return true;
		} else {
			this.compatibleVersion = false;
			return false;
		}
	}

	async getVersion(): Promise<string> {
		let version = await getDoc(doc(this.afs, "appConfig/versioning"));

		if (version.exists()) {
			return version.data()["appVersion"];
		}
		return "";
	}

	async setProfilePicture(url: string, uid: string) {
		if (this.loggedInUser.profilePicture != url) {
			this.loggedInUser.profilePicture = url;
			this.loggedInUser.profilePictureUid = uid;
			this.loggedInUser.profilePictureThumb = url;
			let storageRef = ref(this.storage, `uploads/thumb@256_${uid}`);
			try {
				setTimeout(async () => {
					let url = await getDownloadURL(storageRef);
					this.loggedInUser.profilePictureThumb = url;
					console.log("UPDATING URL: ", url);
				}, 3000);
			} catch (error) {}
			await this.updateUser(this.loggedInUser);
		}
	}

	async updateProfilePictureThumb(url: string, uid: string) {
		this.loggedInUser.profilePicture = url;
		this.loggedInUser.profilePictureUid = uid;
		let storageRef = ref(this.storage, `uploads/thumb@256_${uid}`);
		try {
			let url = await getDownloadURL(storageRef);
			this.loggedInUser.profilePictureThumb = url;
			console.log("UPDATING URL: ", url);
		} catch (error) {}
		this.updateUser(this.loggedInUser);
	}

	async logUserOut() {
		if (this.loggingOut) return;
		this.loggingOut = true;
		const userUid = this.loggedInUser?.uid;
		this.stopWebNotificationListeners();
		try {
			if (isWebBrowser()) {
				await Promise.all([
					this.tryLogoutNotificationCleanup("browser push", () => this.unsubscribeBrowserPush()),
					(async () => {
						if (!userUid) return;
						// Allow a committing registration to finish, but never hold logout indefinitely.
						await this.tryLogoutNotificationCleanup("pending registration", () => Promise.allSettled([...this.pendingFcmTokenWrites]));
						await this.tryLogoutNotificationCleanup("account token", () => this.removeWebFCMToken(userUid));
					})(),
				]);
			}
			await this.afAuth.signOut();
			this.destroyed$.next(null);
			this.clearAuthenticatedState();
			window.location.reload();
		} catch (error) {
			console.error("Sign out failed", error);
			const alert = await this.alertCtrl.create({
				header: "Unable to log out",
				message: "Sign out failed. Please try again.",
				buttons: ["OK"],
			});
			await alert.present();
		} finally {
			this.loggingOut = false;
		}
	}

	private async tryLogoutNotificationCleanup(label: string, cleanup: () => Promise<unknown>): Promise<void> {
		let timeout: ReturnType<typeof setTimeout>;
		try {
			await Promise.race([
				cleanup(),
				new Promise<never>((_, reject) => {
					timeout = setTimeout(() => reject(new Error("Notification cleanup timed out.")), 2000);
				}),
			]);
		} catch (error: any) {
			console.warn(`Web notification ${label} cleanup failed; continuing logout.`, {
				code: error?.code, message: error?.message,
			});
		} finally {
			clearTimeout(timeout);
		}
	}

	private async unsubscribeBrowserPush(): Promise<void> {
		if (!supportsBrowserServiceWorker()) return;
		const registration = await navigator.serviceWorker.getRegistration("/firebase/");
		const worker = registration?.active || registration?.installing || registration?.waiting;
		if (registration?.scope !== new URL("/firebase/", window.location.href).href ||
			worker?.scriptURL !== new URL("/firebase/firebase-messaging-sw.js", window.location.href).href) return;
		await Promise.all([
			(async () => {
				const subscription = await registration.pushManager.getSubscription();
				if (subscription && !(await subscription.unsubscribe())) {
					throw new Error("Browser push subscription could not be removed.");
				}
			})(),
			(async () => {
				const notifications = await registration.getNotifications();
				notifications.forEach((notification) => notification.close());
			})(),
		]);
	}

	async deleteAccount(): Promise<void> {
		const callable = httpsCallable(this.fns, "deleteCentralUserAccount", {
			timeout: 120000,
		});

		this.destroyed$.next(true);

		try {
			await lastValueFrom(from(callable({})));
		} catch (error) {
			console.log("deleteAccount error", error);
			throw error;
		}

		this.clearAuthenticatedState();
		this.clearAccountLocalCache();

		try {
			await this.afAuth.signOut();
		} catch (error) {
			console.log("Local sign out after account deletion failed", error);
		}

		await this.navCtrl.navigateRoot("login");
	}

	private clearAuthenticatedState() {
		this.stopWebNotificationListeners();
		this.loggedIn = false;
		this.authorizedUser = false;
		this.loggedInUser = new User();
		this.loggedInUserPlanUnlockedItems = [];
		this.appUsers = [];
		this.shoppingCart = null;
	}

	private clearAccountLocalCache() {
		try {
			if (typeof localStorage !== "undefined") {
				localStorage.removeItem("clientId");
			}
		} catch (error) {
			console.log("Unable to clear account local cache", error);
		}
	}

	getUserProfile(userUid: string) {
		const foundUser = this.allUsersForFind.find((user) => user.uid === userUid);
		if (foundUser) {
			return foundUser.profilePictureThumb == ""
				? this.defaultUserIcon
				: foundUser.profilePictureThumb;
		}
		return this.defaultUserIcon;
	}

	userSubscribe(userUid: string) {
		return docSnapshots(doc(this.afs, `users/${userUid}`)).pipe(
			map((documentData) => {
				return new User().buildUser(documentData.data() as User);
			}),
		);
	}

	private async addNewUserFromResult(result: UserCredential) {
		return await this.addUser(this.createUserFromCredential(result));
	}

	createUserFromCredential(result: UserCredential) {
		let user = new User();
		user.email = result.user.email;
		user.uid = result.user.uid;
		user.role = "client";
		let names = result.user.displayName.split(" ");
		user.firstName = names.length > 0 ? names[0] : "";
		user.lastName = names.length > 1 ? names[1] : "";
		return user;
	}

	async getUser(userUid: string): Promise<User> {
		console.log("get user...");
		let loggedInUserDoc = await getDoc(doc(this.afs, `users/${userUid}`));
		let user = new User().buildUser(loggedInUserDoc.data() as User);
		if (user.uid !== "") {
			return user;
		}
		return null;
	}

	async checkUserExists(userCred: UserCredential): Promise<any> {
		try {
			const callable = httpsCallable(this.fns, "checkUserExists");

			const res$ = from(
				callable({
					email: userCred.user.email,
					uid: userCred.user.uid,
					displayName: userCred.user.displayName,
				}),
			);

			return await lastValueFrom(res$);
		} catch (error) {
			console.log(error);
			return "error";
		}
	}

	private currentUserEqaulsResult(result: any, currentUser): boolean {
		return (
			result &&
			result.user &&
			result.user.uid &&
			currentUser.uid == result.user.uid
		);
	}

	resetUser(user: User) {
		const result = sendPasswordResetEmail(this.afAuth, user.email).catch(
			(err) => console.log(err),
		);

		return result;
	}

	public validateEmail(email: string) {
		const re =
			/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return re.test(email);
	}

	async logUserIn(user: User) {
		if (this.compatibleVersion) {
			let result = await signInWithEmailAndPassword(
				this.afAuth,
				user.email,
				user.password,
			).catch((err) => {
				return err.message;
			});
			let currentUser = await this.afAuth.currentUser;

			if (
				result &&
				result.user &&
				result.user.uid &&
				currentUser.uid == result.user.uid
			) {
				this.loggedInUser.uid = result.user.uid;
				this.loggedIn = true;
				this.loggedInUser.emailVerified = new Timestamp(
					new Date().getTime() / 1000,
					0,
				);
			} else if (
				result &&
				result.user &&
				result.user.uid &&
				currentUser.uid == result.user.uid
			) {
				this.loggedInUser.uid = result.user.uid;
				result = null;
			}

			const loggedInUserDoc = await getDoc(
				doc(this.afs, `users/${this.loggedInUser.uid}`),
			);

			console.log("fetching user...");
			if (loggedInUserDoc) {
				this.loggedInUser = new User().buildUser(
					loggedInUserDoc.data() as User,
				);

				this.setAuthLevel();
				this.setUserUnlockedItems();

				this.userSub();
			}
			return result;
		} else {
			if (this.serverVersion === "") {
				return "App is offline and failed to get version.";
			}
			return `Update app or close/open new tab in browser to get app version: ${this.serverVersion}`;
		}
	}

	private setAuthLevel() {
		if (
			["admin", "payrollManager", "payroll", "manager", "employee"].includes(
				this.loggedInUser.role,
			)
		) {
			this.authorizedUser = true;
		}
	}

	async getProductWithPlanId(planId: string): Promise<SquareItem> {
		try {
			let querySnapshot = await getDocs(
				query(collection(this.afs, "products"), where("planId", "==", planId)),
			);
			let temp: SquareItem[] = [];
			querySnapshot.forEach((doc) => {
				temp.push(new SquareItem().buildModel(doc.data() as SquareItem));
			});

			return temp.find((t) => t.planId === planId);
		} catch (err) {
			console.log(err);
			return null;
		}
	}

	private async setUserUnlockedItems() {
		if (
			!this.loggedInUser.activeSubscription ||
			this.loggedInUser.subscriptionPlanId === ""
		) {
			this.loggedInUserPlanUnlockedItems = [];
			return;
		}
		const squarePlan = await this.getProductWithPlanId(
			this.loggedInUser.subscriptionPlanId,
		);
		if (!squarePlan) return;

		this.loggedInUserPlanUnlockedItems = squarePlan.planUnlockedItems;
	}

	public isLapsUnlocked(): boolean {
		return this.hasNormalizedFeature("lapTimes");
	}

	public isLiveStreamUnlocked(): boolean {
		return this.hasNormalizedFeature("liveStream");
	}

	public isVideoClipsUnlocked(): boolean {
		return this.hasNormalizedFeature("videoClips");
	}

	private hasNormalizedFeature(feature: SubscriptionFeature): boolean {
		if (!this.hasActiveNormalizedSubscription()) return false;
		if ((this.loggedInUser.currentEntitlements || []).includes(feature)) {
			return true;
		}
		return hasTierAtLeast(
			this.loggedInUser.currentTier || "startingGate",
			getTierForFeature(feature),
		);
	}

	private hasActiveNormalizedSubscription(): boolean {
		const activeStatuses: Array<SubscriptionStatus | ""> = [
			"active",
			"billingRetry",
			"gracePeriod",
		];
		return (
			activeStatuses.includes(this.loggedInUser.subscriptionStatus || "") &&
			hasTierAtLeast(this.loggedInUser.currentTier || "startingGate", "bronze")
		);
	}

	async addUser(user: User) {
		try {
			let obj = user.toObj();
			let res = await setDoc(doc(collection(this.afs, "users"), user.uid), obj);
			return true;
		} catch (err) {
			console.log(err);
			return false;
		}

		// return setDoc(doc(this.afs, `users/${user.uid}`), user.toObj())
		// 	.then((res) => {
		// 		return true;
		// 	})
		// 	.catch((err) => {
		// 		console.log("woops", err);
		// 		return false;
		// 	});
	}

	async deleteUser(user: User) {
		await deleteDoc(doc(this.afs, `users/${user.uid}`));
	}

	async updateUser(user: User) {
		try {
			console.log("+=+=+=+=UPDATE USER+=+=+=+=");
			user.dateModified = new Timestamp(new Date().getTime() / 1000, 0);

			await updateDoc(doc(this.afs, `users/${user.uid}`), user.toObj());

			return user;
		} catch (error) {
			console.log(error);
			return error;
		}
	}

	async setFCMToken(
		token: string,
		platform: string,
		userUid = this.loggedInUser?.uid
	): Promise<void> {
		this.requireNotificationAccount(userUid);
		if (!token) throw new Error("Firebase returned an empty notification token.");
		const write = this.storeFCMToken(token, platform, userUid);
		this.pendingFcmTokenWrites.add(write);
		try {
			await write;
		} finally {
			this.pendingFcmTokenWrites.delete(write);
		}
	}

	private async storeFCMToken(token: string, platform: string, userUid: string): Promise<void> {
		const clientId = await getClientId();
		const userRef = doc(this.afs, `users/${userUid}`);
		const tokens = await runTransaction(this.afs, async (transaction) => {
			this.requireNotificationAccount(userUid);
			const snapshot = await transaction.get(userRef);
			if (!snapshot.exists()) throw new Error("Notification account was not found.");
			const existing: UserFcmToken[] = snapshot.data()["fcmTokens"] || [];
			// Replace this client's entry and any duplicate token; preserve other devices.
			const updated = existing.filter((entry) => entry.clientId !== clientId && entry.token !== token);
			updated.push({ token, platform, clientId });
			transaction.update(userRef, { fcmTokens: updated });
			return updated;
		});
		if (this.loggedInUser?.uid === userUid) this.loggedInUser.fcmTokens = tokens;
	}

	async setWebNotificationPreference(enabled: boolean, userUid = this.loggedInUser?.uid): Promise<void> {
		this.requireNotificationAccount(userUid);
		await updateDoc(doc(this.afs, `users/${userUid}`), {
			"userNotificationConfig.allowMessageNotification": enabled,
		});
		if (this.loggedInUser?.uid === userUid) {
			this.loggedInUser.userNotificationConfig.allowMessageNotification = enabled;
			if (!enabled) this.stopWebNotificationListeners();
		}
	}

	private async removeWebFCMToken(userUid: string): Promise<void> {
		const clientId = localStorage.getItem("clientId");
		if (!clientId) return;
		const userRef = doc(this.afs, `users/${userUid}`);
		await runTransaction(this.afs, async (transaction) => {
			const snapshot = await transaction.get(userRef);
			if (!snapshot.exists()) return;
			const existing: UserFcmToken[] = snapshot.data()["fcmTokens"] || [];
			const remaining = existing.filter((entry) => !(entry.clientId === clientId && entry.platform === "web"));
			if (remaining.length !== existing.length) transaction.update(userRef, { fcmTokens: remaining });
		});
	}

	private requireNotificationAccount(userUid: string): void {
		if (!userUid || this.loggedInUser?.uid !== userUid || this.loggingOut) {
			throw new Error("Notification account changed or is signing out. Please try again.");
		}
	}

	async listenForWebNotifications(userUid: string): Promise<boolean> {
		if (!supportsBrowserNotifications() || !supportsBrowserServiceWorker() ||
			window.Notification.permission !== "granted" || !(await isSupported())) return false;
		if (!this.canPresentWebNotification(userUid)) return false;
		if (this.notificationUserUid === userUid) return true;
		this.stopWebNotificationListeners();
		const parentApp = getApps().find((app) => app.name === "parentApp") ||
			initializeApp(environment.firebaseConfig, "parentApp");
		this.unsubscribeMessages = onMessage(getMessaging(parentApp), (payload) => {
			void this.presentForegroundNotification(payload, userUid)
				.catch((error) => console.error("Foreground notification presentation failed", error));
		});
		this.notificationClickListener = (event) => {
			if (event.data?.action === "navigate" && this.canPresentWebNotification(userUid)) {
				void this.navigateWebNotification(event.data, userUid);
			}
		};
		navigator.serviceWorker.addEventListener("message", this.notificationClickListener);
		this.notificationUserUid = userUid;
		return true;
	}

	private canPresentWebNotification(userUid: string): boolean {
		return !!userUid && !this.loggingOut && this.loggedInUser?.uid === userUid &&
			this.afAuth.currentUser?.uid === userUid &&
			this.loggedInUser.userNotificationConfig.allowMessageNotification !== false;
	}

	private async presentForegroundNotification(payload: MessagePayload, userUid: string): Promise<void> {
		if (document.visibilityState !== "visible" || !this.canPresentWebNotification(userUid)) return;
		const id = payload.messageId;
		if (id && this.presentedNotificationIds.has(id)) return;
		if (id) this.presentedNotificationIds.add(id);
		if (this.presentedNotificationIds.size > 50) this.presentedNotificationIds.delete(this.presentedNotificationIds.values().next().value);
		try {
			const data = payload.data || {};
			const toast = await this.toastCtrl.create({
				header: payload.notification?.title || data["title"] || "FlaggerAI",
				message: payload.notification?.body || data["body"] || "",
				position: "top",
				duration: 10000,
				buttons: [
					...(data["page"] ? [{ text: "View", handler: () => { void this.navigateWebNotification(data, userUid); } }] : []),
					{ text: "Dismiss", role: "cancel" },
				],
			});
			if (document.visibilityState !== "visible" || !this.canPresentWebNotification(userUid)) {
				toast.remove();
				return;
			}
			await toast.present();
		} catch (error) {
			if (id) this.presentedNotificationIds.delete(id);
			throw error;
		}
	}

	private async navigateWebNotification(data: Record<string, string>, userUid: string): Promise<void> {
		if (!this.canPresentWebNotification(userUid)) return;
		try {
			const page = data["page"] === "event" ? "dashboard" : data["page"];
			if (page) await this.navCtrl.navigateForward([page], { queryParams: data });
		} catch (error) {
			console.error("Notification navigation failed", error);
		}
	}

	private stopWebNotificationListeners(): void {
		this.unsubscribeMessages?.();
		this.unsubscribeMessages = undefined;
		if (this.notificationClickListener && supportsBrowserServiceWorker()) {
			navigator.serviceWorker.removeEventListener("message", this.notificationClickListener);
		}
		this.notificationClickListener = undefined;
		this.notificationUserUid = "";
		this.presentedNotificationIds.clear();
	}

	async updateUserTokens(user: User) {
		await updateDoc(doc(this.afs, `users/${user.uid}`), {
			fcmTokens: user.fcmTokens || [],
		});
	}

	async updateUserForSignup(user: User) {
		try {
			console.log("+=+=+=+=UPDATE USER SIGNUP+=+=+=+=");
			user.dateModified = new Timestamp(new Date().getTime() / 1000, 0);

			await updateDoc(doc(this.afs, `users/${user.uid}`), {
				agreedToTerms: true,
				firstName: user.firstName,
				lastName: user.lastName,
				companyName: user.companyName,
				contactInformation:
					user.contactInformation.toObj === undefined
						? user.contactInformation
						: user.contactInformation.toObj(),
			});

			return user;
		} catch (error) {
			console.log(error);
			return error;
		}
	}

	async updateEntryCheckoutBuyerProfile(user: User) {
		const contactInformation: any = user.contactInformation
			? user.contactInformation.toObj === undefined
				? user.contactInformation
				: user.contactInformation.toObj()
			: {};
		const phoneResult = normalizePhoneNumber(
			contactInformation?.cellPhone || contactInformation?.phone || "",
		);
		if (!phoneResult.isEmpty && !phoneResult.e164) {
			throw new Error("Checkout contact phone number is invalid.");
		}
		if (phoneResult.e164) {
			contactInformation.cellPhone = phoneResult.e164;
		}
		await updateDoc(doc(this.afs, `users/${user.uid}`), {
			email: user.email,
			firstName: user.firstName,
			lastName: user.lastName,
			contactInformation,
			dateModified: serverTimestamp(),
		});
	}

	async repairLoggedInUserEmailFromAuthIfMissing() {
		if (this.loggedInUser?.email) {
			return;
		}
		const currentUser = await this.afAuth.currentUser;
		if (!currentUser?.email || !this.loggedInUser?.uid) {
			return;
		}
		this.loggedInUser.email = currentUser.email.toLocaleLowerCase();
		await updateDoc(doc(this.afs, `users/${this.loggedInUser.uid}`), {
			email: this.loggedInUser.email,
			dateModified: serverTimestamp(),
		});
	}

	async updateUserProfile(user: User) {
		await updateDoc(doc(this.afs, `users/${user.uid}`), {
			profilePicture: user.profilePicture,
			profilePictureUid: user.profilePictureUid,
		});
	}

	async isUserLoggedIn(): Promise<boolean> {
		try {
			// If the user is already logged in, resolve immediately
			const currentUser = await this.afAuth.currentUser;
			if (currentUser) {
				return this.fetchAndSetUserDetails(currentUser.uid);
			}

			// If the user is not logged in, wait for token changes
			return new Promise<boolean>((resolve) => {
				this.afAuth.onIdTokenChanged(async (user) => {
					if (user) {
						const success = await this.fetchAndSetUserDetails(user.uid);
						resolve(success);
					} else {
						this.handleNotLoggedIn(resolve);
					}
				});

				// Handle scenario when user is not logged in after 15 seconds
				setTimeout(() => {
					if (!this.loggedIn) {
						resolve(false);
					}
				}, 15000);
			});
		} catch (error) {
			console.log("ERROR IsUserLoggedIn", error);
			return false;
		}
	}

	async isUserSubscriptionActive(): Promise<boolean> {
		return (
			(await this.isUserLoggedIn()) && this.hasActiveNormalizedSubscription()
		);
	}

	private userSub() {
		this.destroyed$.next(null);
		this.userSubscribe(this.loggedInUser.uid)
			.pipe(takeUntil(this.destroyed$))
			.subscribe(async (user) => {
				this.loggedInUser = user;
				// Also receive foreground push after reloading an authenticated page other than Dashboard.
				void this.listenForWebNotifications(user.uid)
					.catch((error) => console.error("Web notification listener setup failed", error));
				await this.setUserUnlockedItems();
			});
	}

	private async fetchAndSetUserDetails(uid: string): Promise<boolean> {
		this.loggedInUser.uid = uid;
		this.loggedIn = true;
		this.loggedInUser.emailVerified = new Timestamp(
			new Date().getTime() / 1000,
			0,
		);
		try {
			const loggedInUserDoc = await getDoc(doc(this.afs, `users/${uid}`));

			console.log("fetching user...");
			if (loggedInUserDoc) {
				this.loggedInUser = new User().buildUser(
					loggedInUserDoc.data() as User,
				);

				this.setAuthLevel();
				this.setUserUnlockedItems();

				this.userSub();

				return true;
			} else {
				return false;
			}
		} catch (err) {
			console.log(err);
			return false;
		}
	}

	private handleNotLoggedIn(resolve: (value: boolean) => void): void {
		this.loggedIn = false;
		resolve(false);
	}

	getUserName(userUid: string) {
		const foundUser = this.allUsersForFind.find((user) => user.uid === userUid);
		if (foundUser) {
			const userFullName = `${foundUser.firstName} ${foundUser.lastName}`;
			return userFullName;
		}
		return "No User Set";
	}

	async getUserNameContact(userUid: string) {
		if (userUid === "bot") {
			return "Flagger";
		}
		const foundUser = this.allUsersForFind.find((user) => user.uid === userUid);
		if (foundUser) {
			const userFullName = `${foundUser.firstName} ${foundUser.lastName}`;
			return userFullName;
		}
		return "No User Set";
	}

	async getUserProfileContact(userUid: string) {
		if (userUid === "bot") {
			return "assets/flagger-logo-new.png";
		}
		const foundUser = this.allUsersForFind.find((user) => user.uid === userUid);
		if (foundUser) {
			return foundUser.profilePictureThumb == ""
				? this.defaultUserIcon
				: foundUser.profilePictureThumb;
		}
		return this.defaultUserIcon;
	}

	async addAgreement(agreement: AgreedToEula) {
		let newDoc = doc(collection(this.afs, "userEulaAgreements"));
		agreement.uid = newDoc.id;
		await setDoc(newDoc, agreement.toObj());
	}

	async getAgreementsForUser(userUid: string): Promise<AgreedToEula[]> {
		let querySnapshot = await getDocs(
			query(
				collection(this.afs, "userEulaAgreements"),
				where("userUid", "==", userUid),
			),
		);
		let matching: AgreedToEula[] = [];
		querySnapshot.forEach((doc) => {
			matching.push(new AgreedToEula().buildModel(doc.data() as AgreedToEula));
		});
		return matching;
	}

	async isUserEulaAgreementUpToDate(userUid: string) {
		let isUpToDate: boolean = false;
		let agreements = await this.getAgreementsForUser(userUid);

		for (let index = 0; index < agreements.length; index++) {
			const agreement = agreements[index];
			if (hasUserAgreedToMostRecent(agreement.eulaVersion, this.eulaVersion)) {
				isUpToDate = true;
			}
		}

		return isUpToDate;

		function hasUserAgreedToMostRecent(
			userVersionString: string,
			eulaVersion: string,
		) {
			const userVersion = parseFloat(userVersionString);
			const recentVersion = parseFloat(eulaVersion);
			return userVersion >= recentVersion;
		}
	}

	public async checkEula() {
		const userUid =
			this.loggedInUser?.uid || (await this.afAuth.currentUser)?.uid;
		if (!userUid) return;

		const upToDate = await this.isUserEulaAgreementUpToDate(userUid);

		if (upToDate) {
			await this.ensureAgreedToTermsField(userUid);
			return;
		}

		await this.openEulaAgreement();
	}

	private async openEulaAgreement() {
		const modal = await this.modalCtrl.create({
			component: EulaAgreementModal,
			cssClass: "fullscreen-modal-css",
		});
		await modal.present();
		const { data, role } = await modal.onDidDismiss();

		if (role === "confirm") {
			console.log("EULA was accepted:", data);

			let agreement = new AgreedToEula(this.loggedInUser.uid, this.eulaVersion);
			await this.addAgreement(agreement);
			await this.ensureAgreedToTermsField(this.loggedInUser.uid, true);
		} else {
			const alert = await this.alertCtrl.create({
				header: "Logging User Out",
				message: `We're sorry to see you go. You must agree to the terms to continue using this software. If you have any questions, please contact support.`,
				buttons: ["Ok"],
			});

			await alert.present();
			await alert.onDidDismiss();

			await this.logUserOut();
		}
	}

	private async ensureAgreedToTermsField(userUid: string, force = false) {
		if (!userUid) return;
		if (!force && this.loggedInUser?.agreedToTerms === true) return;

		await updateDoc(doc(this.afs, `users/${userUid}`), {
			agreedToTerms: true,
			dateModified: serverTimestamp(),
		});

		if (this.loggedInUser?.uid === userUid) {
			this.loggedInUser.agreedToTerms = true;
		}
	}

	async navAfterLogin(returnUrl: string = "") {
		await this.checkEula();
		if (this.shoppingCart === null && this.loggedInUser.shoppingCart !== null) {
			this.shoppingCart = new SquareItem().buildModel(
				this.loggedInUser.shoppingCart as SquareItem,
			);
		}
		if (this.shoppingCart !== null) {
			this.navCtrl.navigateForward("cart");
		} else {
			this.navCtrl.navigateRoot(
				returnUrl || this.requestedRoute || "dashboard",
			);
		}
	}

	async clearSubscriptionCheckoutState(): Promise<void> {
		this.shoppingCart = null;
		this.selectedProduct = "";
		this.requestedRoute = "";
		this.refreshRequestedRoute = "";
		this.refreshRequestedTab = "";

		if (!this.loggedInUser?.uid) return;

		this.loggedInUser.shoppingCart = null;
		await updateDoc(doc(this.afs, `users/${this.loggedInUser.uid}`), {
			shoppingCart: null,
		});
	}

	async navAfterSignup(returnUrl: string = "") {
		if (this.shoppingCart === null && this.loggedInUser.shoppingCart !== null) {
			this.shoppingCart = new SquareItem().buildModel(
				this.loggedInUser.shoppingCart as SquareItem,
			);
		}

		if (this.shoppingCart !== null) {
			this.navCtrl.navigateForward("cart");
			return;
		}

		const targetRoute = returnUrl || this.requestedRoute || "";
		this.navCtrl.navigateRoot(["subscription-access"], {
			queryParams: {
				onboarding: "signup",
				...(targetRoute ? { returnUrl: targetRoute } : {}),
			},
		});
	}

	async registerUser(user: User, role: string = "client") {
		if (user.email === "") {
			return {
				code: "auth/argument-error",
			};
		}
		user.email = user.email.toLocaleLowerCase();

		if (this.compatibleVersion) {
			let code = "";
			const result = await createUserWithEmailAndPassword(
				this.afAuth,
				user.email,
				user.password,
			).catch((err) => {
				console.log("Error", err);
				code = err.code;
				return err.message;
			});

			if (code) {
				return {
					code: code,
					message: result,
				};
			}

			user.uid = result.user.uid;
			this.loggedIn = true;
			user.role = role;
			if (this.shoppingCart !== null) {
				user.shoppingCart = this.shoppingCart;
			}
			let newUser = new User();
			let userObj = newUser.buildUser(user);
			delete userObj.password;
			const addedUser = await this.addUser(userObj);

			if (result && result.user && result.user.uid && addedUser) {
				this.loggedInUser.uid = result.user.uid;
				this.loggedInUser.emailVerified = new Timestamp(
					new Date().getTime() / 1000,
					0,
				);

				this.loggedInUser = user;

				this.userSub();
			} else {
				return "Error registering";
			}
			return result;
		} else {
			return `Update app or close/open new tab in browser to get app version: ${this.serverVersion}`;
		}
	}

	async getAllAppUsersFromInstance(afs: Firestore): Promise<Array<AppUser>> {
		let querySnapshot = await getDocs(query(collection(afs, "appUsers")));

		return querySnapshot.docs.reduce((arr, doc) => {
			let usr = new AppUser().buildUser(doc.data() as AppUser);

			arr.push(usr);
			return arr;
		}, []);
	}

	async getAppUsersByUids(userUids: string[]): Promise<AppUser[]> {
		const uniqueUids = Array.from(new Set(userUids.filter(Boolean)));

		if (uniqueUids.length === 0) {
			return [];
		}

		const userMap = new Map<string, AppUser>();

		for (let index = 0; index < uniqueUids.length; index += 10) {
			const uidBatch = uniqueUids.slice(index, index + 10);

			const querySnapshot = await getDocs(
				query(
					collection(this.afs, "appUsers"),
					where(documentId(), "in", uidBatch),
				),
			);

			querySnapshot.forEach((docSnap) => {
				const user = new AppUser().buildUser(docSnap.data() as AppUser);

				if (!user.uid) {
					user.uid = docSnap.id;
				}

				userMap.set(user.uid, user);
			});
		}

		return uniqueUids
			.map((uid) => userMap.get(uid))
			.filter((user): user is AppUser => !!user);
	}

	async loadClaimedRidersForCurrentUser(): Promise<AppUser[]> {
		const result = await httpsCallable<void, RiderEmail | null>(this.fns, "getManagedRiderMapping")();
		return this.getAppUsersByUids(result.data?.appUserUids || []);
	}

	async createCentralRiderProfile(
		request: CreateCentralRiderProfileRequest,
	): Promise<CreateCentralRiderProfileResponse> {
		const callable = httpsCallable(this.fns, "createCentralRiderProfile", {
			timeout: 60000,
		});
		const res = await lastValueFrom(from(callable(request)));
		const data = (res.data || {}) as any;
		return {
			appUser: new AppUser().buildUser(data.appUser as AppUser),
			riderEmail: new RiderEmail().buildModel(data.riderEmail as RiderEmail),
		};
	}

	async updateAppUserProfile(appUser: AppUser): Promise<void> {
		await updateDoc(doc(this.afs, `appUsers/${appUser.uid}`), {
			firstName: appUser.firstName,
			lastName: appUser.lastName,
			riderNumber: appUser.riderNumber,
			bike: appUser.bike,
			dob: appUser.dob || "",
		});
	}

	async getRiderPrivateContact(riderUid: string): Promise<any> {
		const callable = httpsCallable(this.fns, "getRiderPrivateContact");
		const res: any = await lastValueFrom(from(callable({ riderUid })));
		return res.data;
	}

	async saveRiderPrivateContact(riderUid: string, contact: any): Promise<void> {
		const callable = httpsCallable(this.fns, "saveRiderPrivateContact", {
			timeout: 60000,
		});
		await lastValueFrom(from(callable({ riderUid, contact })));
	}

	async setActiveAppUser(appUserUid: string): Promise<void> {
		await updateDoc(doc(this.afs, `appUsers/${appUserUid}`), {
			lastActiveRiderTime: serverTimestamp(),
		});
	}

	getMostRecentAppUser(appUsers: AppUser[]): AppUser | undefined {
		if (!appUsers || appUsers.length === 0) {
			return undefined;
		}

		return appUsers.reduce((activeUser, currentUser) => {
			const activeTime = this.getTimestampMillis(
				activeUser.lastActiveRiderTime,
			);
			const currentTime = this.getTimestampMillis(
				currentUser.lastActiveRiderTime,
			);

			return currentTime > activeTime ? currentUser : activeUser;
		});
	}

	private getTimestampMillis(timestamp: any): number {
		if (!timestamp) {
			return 0;
		}

		if (typeof timestamp.toMillis === "function") {
			return timestamp.toMillis();
		}

		if (typeof timestamp.toDate === "function") {
			return timestamp.toDate().getTime();
		}

		if (typeof timestamp.seconds === "number") {
			return timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1e6;
		}

		if (typeof timestamp._seconds === "number") {
			return timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1e6;
		}

		return 0;
	}

	async unclaimAppUser(
		riderEmailUid: string, appUserUid: string, expectedRevision: number, requestId: string,
	): Promise<{ success: boolean; completion: "complete" | "partial" }> {
		const result = await httpsCallable<unknown, { success: boolean; completion: "complete" | "partial" }>(this.fns, "unclaimAppUser")({
			riderEmailUid, appUserUid, expectedRevision, requestId,
		});
		return result.data;
	}

	newRiderLinkRequestId(): string { return doc(collection(this.afs, "appUsers")).id; }

	public async syncAppUsersAcrossInstances(
		request?: RiderMigrationRequest,
	): Promise<RiderMigrationResponse | any> {
		try {
			const callable = httpsCallable<
				RiderMigrationRequest | Record<string, never>,
				RiderMigrationResponse
			>(this.fns, "syncAppUsersAcrossInstances");

			const res$ = from(callable(request || {}));

			const res = await lastValueFrom(res$);
			console.log("syncAppUsersAcrossInstances result", res);
			return res.data;
		} catch (error) {
			console.log("syncAppUsersAcrossInstances error", error);
			return error;
		}
	}

	async setAllAppUsersGlobal(afs: Firestore) {
		console.log("get appUsers...");
		this.appUsers = [];
		this.appUsers = await this.getAllAppUsersFromInstance(afs);
	}

	getUserByEPC(EPC: string): AppUser {
		let user = this.appUsers.find((u) => u.getEpcCodes().includes(EPC));
		return user;
	}

	findAppUsersByEPC(EPC: string): AppUser[] {
		const normalizedEpc = String(EPC || "")
			.trim()
			.toUpperCase()
			.substring(0, 24);

		if (!normalizedEpc) {
			return [];
		}

		return (this.appUsers || []).filter((user) =>
			(user.getEpcCodes() || [])
				.map((epc) =>
					String(epc || "")
						.trim()
						.toUpperCase()
						.substring(0, 24),
				)
				.includes(normalizedEpc),
		);
	}

	async getAllUsers() {
		console.log("getting all users");

		let querySnapshot = await getDocs(query(collection(this.afs, "users")));
		let temp: User[] = [];
		querySnapshot.forEach((doc) => {
			temp.push(new User().buildUser(doc.data() as User));
		});
		return temp;
	}

	public async updateAllAppUsers() {
		const startTime = Date.now();
		let allUsers = await this.getAllUsers();

		for (let index = 0; index < allUsers.length; index++) {
			const user = allUsers[index];
			console.log(user.email);
			await this.updateAppUser(user.uid);
			estimateRemainingTime(allUsers, index, startTime);
		}
		console.log("🥓🥓🥓 Complete 🥓🥓🥓");
	}

	private async updateAppUser(userUid: string) {
		try {
			let callable = httpsCallable(this.fns, "setAppUserSubscription");
			let res$ = from(
				callable({
					userUid: userUid,
				}),
			);

			let res = await lastValueFrom(res$);
			console.log("res", res);

			return res;
		} catch (error) {
			console.log(error);
			return [];
		}
	}
}
